import { createSlice } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { RootState } from "../../app/store";
import { Organization } from "./models/Organization";
import { OrganizationInvitation } from "./models/OrganizationInvitation";
import { OrganizationInvitationPreview } from "./models/OrganizationInvitationPreview";
import {
  OrganizationApiError,
  acceptInvitation,
  createInvitation,
  fetchCurrentOrganization,
  fetchInvitationPreview,
  fetchPendingInvitations,
  renameOrganization,
  resetVocabularyTerm,
  revokeInvitation,
  setVocabularyTerm,
  transferOwnership,
  updateOrganizationProfile,
} from "./slice-extensions/organization-slice-extension";
import {
  applyFahrschulePreset,
  chooseOrganizationKind,
} from "./slice-extensions/organization-preset-slice-extension";
import { ApplyPresetResponse } from "./contracts/ApplyPresetResponse";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";

interface OrganizationState {
  organization: Organization | null;
  invitations: OrganizationInvitation[];
  invitationPreview: OrganizationInvitationPreview | null;
  status: LoadingStatus;
  previewStatus: LoadingStatus;
  acceptStatus: LoadingStatus;
  lastError?: OrganizationApiError;
  previewError?: OrganizationApiError;
  acceptError?: OrganizationApiError;
  vocabularyError?: OrganizationApiError;
  /** The last Fahrschule preset run: what it created, skipped and could not do. */
  presetResult: ApplyPresetResponse | null;
  presetStatus: LoadingStatus;
  presetError?: ApiErrorResponse;
}

const initialState: OrganizationState = {
  organization: null,
  invitations: [],
  invitationPreview: null,
  status: LoadingStatus.Idle,
  previewStatus: LoadingStatus.Idle,
  acceptStatus: LoadingStatus.Idle,
  lastError: undefined,
  previewError: undefined,
  acceptError: undefined,
  vocabularyError: undefined,
  presetResult: null,
  presetStatus: LoadingStatus.Idle,
  presetError: undefined,
};

const organizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    clearOrganizationError: (state) => {
      state.lastError = undefined;
    },
    resetAcceptState: (state) => {
      state.acceptStatus = LoadingStatus.Idle;
      state.acceptError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentOrganization.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchCurrentOrganization.fulfilled, (state, action) => {
        state.status = LoadingStatus.Idle;
        state.organization = action.payload;
      })
      .addCase(fetchCurrentOrganization.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      });

    builder
      .addCase(renameOrganization.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(renameOrganization.fulfilled, (state, action) => {
        state.status = LoadingStatus.Idle;
        state.organization = action.payload;
      })
      .addCase(renameOrganization.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      });

    builder
      .addCase(transferOwnership.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(transferOwnership.fulfilled, (state, action) => {
        state.status = LoadingStatus.Idle;
        state.organization = action.payload;
      })
      .addCase(transferOwnership.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      });

    // Kind, language and vocabulary saves update the organization in place,
    // without flipping the shared loading status (no card flicker).
    for (const thunk of [updateOrganizationProfile, setVocabularyTerm, resetVocabularyTerm]) {
      builder
        .addCase(thunk.pending, (state) => {
          state.vocabularyError = undefined;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.organization = action.payload;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.vocabularyError = action.payload;
        });
    }

    builder
      .addCase(applyFahrschulePreset.pending, (state) => {
        state.presetStatus = LoadingStatus.Loading;
        state.presetError = undefined;
      })
      .addCase(applyFahrschulePreset.fulfilled, (state, action) => {
        state.presetStatus = LoadingStatus.Idle;
        state.presetResult = action.payload;
      })
      .addCase(applyFahrschulePreset.rejected, (state, action) => {
        state.presetStatus = LoadingStatus.Failed;
        state.presetError = action.payload;
      })
      .addCase(chooseOrganizationKind.fulfilled, (state, action) => {
        state.organization = action.payload;
      });

    builder
      .addCase(fetchPendingInvitations.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchPendingInvitations.fulfilled, (state, action) => {
        state.status = LoadingStatus.Idle;
        state.invitations = action.payload;
      })
      .addCase(fetchPendingInvitations.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      });

    builder
      .addCase(createInvitation.fulfilled, (state, action) => {
        state.invitations.push(action.payload);
      })
      .addCase(revokeInvitation.fulfilled, (state, action) => {
        state.invitations = state.invitations.filter(
          (invitation) => invitation.id !== action.payload.invitationId
        );
      });

    builder
      .addCase(fetchInvitationPreview.pending, (state) => {
        state.previewStatus = LoadingStatus.Loading;
        state.previewError = undefined;
        state.invitationPreview = null;
      })
      .addCase(fetchInvitationPreview.fulfilled, (state, action) => {
        state.previewStatus = LoadingStatus.Idle;
        state.invitationPreview = action.payload;
      })
      .addCase(fetchInvitationPreview.rejected, (state, action) => {
        state.previewStatus = LoadingStatus.Failed;
        state.previewError = action.payload;
      });

    builder
      .addCase(acceptInvitation.pending, (state) => {
        state.acceptStatus = LoadingStatus.Loading;
        state.acceptError = undefined;
      })
      .addCase(acceptInvitation.fulfilled, (state) => {
        state.acceptStatus = LoadingStatus.Idle;
      })
      .addCase(acceptInvitation.rejected, (state, action) => {
        state.acceptStatus = LoadingStatus.Failed;
        state.acceptError = action.payload;
      });
  },
});

export const { clearOrganizationError, resetAcceptState } =
  organizationSlice.actions;

export const selectOrganization = (state: RootState) =>
  state.organization.organization;
export const selectOrganizationStatus = (state: RootState) =>
  state.organization.status;
export const selectOrganizationError = (state: RootState) =>
  state.organization.lastError;
export const selectOrganizationInvitations = (state: RootState) =>
  state.organization.invitations;
export const selectInvitationPreview = (state: RootState) =>
  state.organization.invitationPreview;
export const selectInvitationPreviewStatus = (state: RootState) =>
  state.organization.previewStatus;
export const selectInvitationPreviewError = (state: RootState) =>
  state.organization.previewError;
export const selectAcceptInvitationStatus = (state: RootState) =>
  state.organization.acceptStatus;
export const selectVocabularyError = (state: RootState) =>
  state.organization.vocabularyError;
export const selectPresetResult = (state: RootState) =>
  state.organization.presetResult;
export const selectPresetStatus = (state: RootState) =>
  state.organization.presetStatus;
export const selectPresetError = (state: RootState) =>
  state.organization.presetError;
export const selectAcceptInvitationError = (state: RootState) =>
  state.organization.acceptError;

export default organizationSlice.reducer;
